export function summariseRevenueSplit(days = []) {
  const categories = new Map()
  let oneTotal = 0
  let oneDays = 0
  let revenue = 0

  for (const day of days) {
    const sale = Number(day?.sale)
    const lines = Array.isArray(day?.revenueLines) ? day.revenueLines : []
    if (lines.length) {
      const seen = new Set()
      for (const line of lines) {
        const name = String(line?.name || 'Category')
        const amount = Number(line?.calculated) || 0
        const current = categories.get(name) || { name, total: 0, days: 0 }
        current.total += amount
        if (!seen.has(name)) {
          current.days += 1
          seen.add(name)
        }
        categories.set(name, current)
      }
      revenue += lines.reduce(
        (sum, line) => sum + (Number(line?.calculated) || 0),
        0,
      )
    } else if (Number.isFinite(sale) && sale !== 0) {
      oneTotal += sale
      oneDays += 1
      revenue += sale
    }
  }

  const share = amount => (revenue ? amount / revenue : 0)
  const rows = [...categories.values()]
    .map(row => ({ ...row, share: share(row.total), oneTotal: false }))
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name))

  if (oneDays) {
    rows.push({
      name: 'Recorded as one total',
      total: oneTotal,
      days: oneDays,
      share: share(oneTotal),
      oneTotal: true,
    })
  }

  return { rows, revenue }
}

export function previousPeriodRange(from, to) {
  const start = new Date(`${from}T00:00:00Z`)
  const end = new Date(`${to}T00:00:00Z`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null
  const length = Math.round((end - start) / 86400000) + 1
  if (length < 1) return null
  const previousEnd = new Date(start)
  previousEnd.setUTCDate(previousEnd.getUTCDate() - 1)
  const previousStart = new Date(previousEnd)
  previousStart.setUTCDate(previousStart.getUTCDate() - (length - 1))
  const format = date => date.toISOString().slice(0, 10)
  return { from: format(previousStart), to: format(previousEnd) }
}

export function attachPreviousPeriod(currentRows = [], previous = null) {
  if (!previous || !previous.revenue) {
    return { empty: true, rows: currentRows }
  }
  const previousByName = new Map(
    (previous.rows || []).map(row => [row.name, row]),
  )
  const seen = new Set()
  const rows = []
  for (const row of currentRows.filter(item => !item.oneTotal)) {
    seen.add(row.name)
    const prior = previousByName.get(row.name)
    rows.push({
      ...row,
      previousTotal: prior?.total || 0,
      previousShare: prior?.share || 0,
    })
  }
  for (const row of previous.rows || []) {
    if (row.oneTotal || seen.has(row.name)) continue
    rows.push({
      name: row.name,
      total: 0,
      days: 0,
      share: 0,
      oneTotal: false,
      previousTotal: row.total,
      previousShare: row.share,
    })
  }
  const currentOne = currentRows.find(row => row.oneTotal)
  const priorOne = previousByName.get('Recorded as one total')
  if (currentOne || priorOne) {
    rows.push({
      name: 'Recorded as one total',
      total: currentOne?.total || 0,
      days: currentOne?.days || 0,
      share: currentOne?.share || 0,
      oneTotal: true,
      previousTotal: priorOne?.total || 0,
      previousShare: priorOne?.share || 0,
    })
  }
  return { empty: false, rows }
}

export function salesSplitHeadline(rows = []) {
  return (rows || []).find(row => row && !row.oneTotal) || null
}

const entryLabel = line => {
  const multiplier = Number(line?.multiplier)
  const entry = line?.entry
  const figure = entry == null || entry === '' ? '' : String(entry)
  if (!Number.isFinite(multiplier) || multiplier === 1) {
    return figure ? `Amount · ${figure}` : 'Amount'
  }
  const factor = multiplier.toFixed(2)
  return figure ? `Quantity × ${factor} · ${figure}` : `Quantity × ${factor}`
}

/** One spreadsheet row per day and category. One-total days keep that label. */
export function revenueSplitExportRows(days = []) {
  const summary = summariseRevenueSplit(days)
  const byName = new Map(summary.rows.map(row => [row.name, row]))
  const sorted = [...days].sort((a, b) =>
    String(a?.date).localeCompare(String(b?.date)),
  )
  const rows = []

  for (const day of sorted) {
    const lines = Array.isArray(day?.revenueLines) ? day.revenueLines : []
    const sale = Number(day?.sale)
    if (lines.length) {
      for (const line of lines) {
        const name = String(line?.name || 'Category')
        const period = byName.get(name)
        rows.push({
          date: day?.date || '',
          category: name,
          entry: entryLabel(line),
          lineTotal: Number(line?.calculated) || 0,
          periodTotal: period?.total || 0,
          share: period?.share || 0,
        })
      }
    } else if (Number.isFinite(sale) && sale !== 0) {
      const period = byName.get('Recorded as one total')
      rows.push({
        date: day?.date || '',
        category: 'Recorded as one total',
        entry: '',
        lineTotal: sale,
        periodTotal: period?.total || 0,
        share: period?.share || 0,
      })
    }
  }

  return rows
}

export function compareRevenueCategories(days = [], names = []) {
  const chosen = names.filter(Boolean)
  if (chosen.length < 2) return []

  return days
    .map(day => {
      const lines = Array.isArray(day?.revenueLines) ? day.revenueLines : []
      const sale = Number(day?.sale)
      const lineTotal = lines.reduce(
        (sum, line) => sum + (Number(line?.calculated) || 0),
        0,
      )
      const dayRevenue = Number.isFinite(sale) && sale !== 0 ? sale : lineTotal
      if (!dayRevenue) return null
      const oneTotal = !lines.length
      const cells = {}
      const rest = []
      if (!oneTotal) {
        const chosenSet = new Set(chosen)
        for (const name of chosen) {
          const matched = lines.filter(
            line => String(line?.name || 'Category') === name,
          )
          if (!matched.length) continue
          const amount = matched.reduce(
            (sum, line) => sum + (Number(line?.calculated) || 0),
            0,
          )
          cells[name] = {
            amount,
            share: dayRevenue ? amount / dayRevenue : 0,
          }
        }
        const other = new Map()
        for (const line of lines) {
          const name = String(line?.name || 'Category')
          if (chosenSet.has(name)) continue
          other.set(
            name,
            (other.get(name) || 0) + (Number(line?.calculated) || 0),
          )
        }
        for (const [name, amount] of other) {
          if (Math.abs(amount) <= 0.01) continue
          rest.push({
            name,
            amount,
            share: dayRevenue ? amount / dayRevenue : 0,
          })
        }
        const gap = Math.round((dayRevenue - lineTotal) * 100) / 100
        if (Math.abs(gap) > 0.01) {
          rest.push({
            name: 'Not in a category',
            amount: gap,
            share: dayRevenue ? gap / dayRevenue : 0,
          })
        }
      }
      return { date: day.date, dayRevenue, oneTotal, cells, rest }
    })
    .filter(Boolean)
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
}

/** Pounds on a lined day that are not in a category. Null when the lines match the day, or there are no lines. */
export function uncategorisedRevenue(sale, lines) {
  const rows = Array.isArray(lines) ? lines : []
  if (!rows.length) return null
  const dayRevenue = Number(sale)
  if (!Number.isFinite(dayRevenue)) return null
  const lineTotal = rows.reduce(
    (sum, line) => sum + (Number(line?.calculated) || 0),
    0,
  )
  const gap = Math.round((dayRevenue - lineTotal) * 100) / 100
  if (Math.abs(gap) <= 0.01) return null
  return gap
}

/** Saving lines would replace a saved day figure that differs by more than a penny. */
export function saleFigureLinesWouldReplace(savedSale, lineTotal) {
  const saved = Number(savedSale)
  if (!Number.isFinite(saved) || saved === 0) return null
  const next = lineTotal == null || lineTotal === '' ? 0 : Number(lineTotal)
  if (!Number.isFinite(next)) return null
  const gap = Math.round((saved - next) * 100) / 100
  if (Math.abs(gap) <= 0.01) return null
  return { saved, next, gap }
}
export function isRecordedAsOneTotal(sale, lines) {
  if (!Array.isArray(lines) || lines.length > 0) return false
  const amount = Number(sale)
  return Number.isFinite(amount) && amount !== 0
}
